import { useMemo, useRef, useState, useEffect } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { OtpMethod } from "../domain/models/OtpMethod";
import type { OtpState } from "../domain/models/OtpState";
import { authService } from "../../../api/services/authService";
import { tokenService } from "../../../api/utils/tokenService";
import type { VerifyOtpErrorResponse } from "../data/dto/VerifyOtpErrorResponse";
export function useOtpViewModel(
  method: OtpMethod = "email",
  destination: string = ""
) {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));

  const inputRefs = useRef<HTMLInputElement[]>([]);

  const [rememberDevice, setRememberDevice] = useState(true);

  const [state, setState] = useState<OtpState>("normal");

  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const [remainingResends, setRemainingResends] = useState(3);


  const [retryAfterSeconds, setRetryAfterSeconds] =
    useState(0);
  const [showHelp, setShowHelp] = useState(false);

  const navigate = useNavigate();
  const isOtpComplete = useMemo(
    () => otp.every((digit) => digit !== ""),
    [otp]
  );

  const [otpError, setOtpError] =
    useState<VerifyOtpErrorResponse | null>(null);

  const canResend =
    secondsRemaining === 0 && remainingResends > 0;

  const handleOtpChange = (
    index: number,
    value: string
  ) => {
    if (!/^\d?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (
      value &&
      index < inputRefs.current.length - 1
    ) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      e.key === "Backspace" &&
      otp[index] === "" &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyOtp = async () => {
    try {
      const response =
        await authService.verifyOtp({
          identifier: destination,
          code: otp.join(""),
          otpChannel: "WEB",
          rememberDevice,
        });

      const data = response.data;

      tokenService.setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });

      if (data.deviceToken) {
        localStorage.setItem(
          "deviceToken",
          data.deviceToken
        );
      }

      localStorage.setItem(
        "partner",
        JSON.stringify(data.partner)
      );

      console.log("Login Success");

      // navigate
      if (!data.partner.accountTypeId) {
        navigate("/account-type");
      } else {
        navigate("/dashboard");
      }
    } catch (err: any) {

      const data = err.payload as VerifyOtpErrorResponse;

      setOtpError(data);

      setRetryAfterSeconds(
        data.retryAfterSeconds ?? 0
      );

      setState("wrong");
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    await authService.sendOtp({
      identifier: destination,
      otpChannel: "WEB",
    });

    setOtp(Array(6).fill(""));
    setSecondsRemaining(60);
    setRemainingResends((prev) => prev - 1);
  };

  useEffect(() => {
    if (secondsRemaining <= 0) return;

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsRemaining]);



  useEffect(() => {
    if (retryAfterSeconds <= 0) return;

    const timer = setTimeout(() => {
      setRetryAfterSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [retryAfterSeconds]);

  const openHelp = () => {
    setShowHelp(true);
  };

  return {
    method,
    destination,

    otp,
    inputRefs,

    rememberDevice,
    setRememberDevice,

    state,
    setState,

    secondsRemaining,
    setSecondsRemaining,

    remainingResends,

    showHelp,
    setShowHelp,

    isOtpComplete,

    canResend,

    handleOtpChange,
    handleKeyDown,

    verifyOtp,
    handleResend,

    otpError,
    retryAfterSeconds,
    openHelp,
  };
}